import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatMoney, t } from "@/i18n";
import { TrendingUp, Vault, Users, Handshake } from "lucide-react";

function AnimatedNumber({ value, format = (v) => v.toString(), duration = 1600 }) {
  const [display, setDisplay] = useState(0);
  const started = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const from = 0;
        const to = Number(value) || 0;
        const tick = (t) => {
          const p = Math.min(1, (t - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(from + (to - from) * eased);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{format(display)}</span>;
}

export default function LiveStats() {
  const { lang } = useApp();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const labels = {
    volume: { it: "Volume trattato", en: "Volume transacted", he: "היקף עסקאות", ru: "Оборот сделок" },
    escrow: { it: "Escrow gestito", en: "Escrow managed", he: "נאמנות מנוהלת", ru: "В эскроу управлено" },
    deals: { it: "Trattative", en: "Deals", he: "עסקאות", ru: "Сделки" },
    members: { it: "Membri", en: "Members", he: "חברים", ru: "Участники" },
  };
  const fmt = (v) => formatMoney(Math.round(v), lang);
  const fmtInt = (v) => Math.round(v).toLocaleString();

  const items = [
    { icon: TrendingUp, key: "volume", value: stats?.total_volume || 0, format: fmt },
    { icon: Vault, key: "escrow", value: stats?.escrow_managed || 0, format: fmt },
    { icon: Handshake, key: "deals", value: stats?.deals_count || 0, format: fmtInt },
    { icon: Users, key: "members", value: stats?.members || 0, format: fmtInt },
  ];

  return (
    <section className="py-20 sm:py-24 border-t border-[#D8CFBB] bg-[#FFFAF0]" data-testid="live-stats-section">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl mb-12">
          <p className="text-xs tracking-[0.4em] uppercase text-[#C5A880] mb-3">
            {t("trust_in_numbers", lang)}
          </p>
          <h2 className="text-3xl sm:text-4xl font-light text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {t("trust_in_numbers_sub", lang)}
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.key} data-testid={`stat-${it.key}`}
              className="rounded-lg border border-[#D8CFBB] bg-[#F5F0E6] p-6 hover:border-[#C5A880] hover:-translate-y-1 transition-transform duration-300">
              <div className="flex items-center justify-between mb-6">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">{labels[it.key][lang]}</p>
                <it.icon className="h-4 w-4 text-[#C5A880]" />
              </div>
              <p className="text-3xl sm:text-4xl font-light text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                <AnimatedNumber value={it.value} format={it.format} />
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
